import React from "react";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { RiskModelPicker } from "./VqqFiltersBar";

/**
 * Admin-only affordance for VQQs that have never been risk-scored — typically
 * historical questionnaires finalized before the Risk Scoring Engine shipped.
 *
 * Surfaces the same model picker used inside the Q&RA filter bar plus a
 * primary "Run risk scoring" button. On success the parent invalidates the
 * scored-snapshot query and the banner is replaced by the full risk surface
 * (PublishBar + RiskOverviewCard + filters + per-question Risk Panel).
 */
const RunRiskScoringBanner = ({
  availableModels,
  viewerKind = "admin",
  onSelectModel,
  onScoreNow,
  isScoring = false,
}) => {
  return (
    <div className="rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white px-4 py-4 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
            <ShieldExclamationIcon className="h-5 w-5" aria-hidden="true" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Risk scoring hasn&apos;t been generated for this VQQ
            </p>
            <p className="mt-0.5 text-[13px] text-gray-600 leading-relaxed">
              {isScoring
                ? "Running the risk engine against the vendor's responses — this can take up to a minute. The page will update automatically."
                : "Pick a risk model and run the engine to generate finding severities, criticality bands, and the analysis report. The result lands as a draft snapshot you can review and publish."}
            </p>
          </div>
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-3 lg:justify-end">
          <RiskModelPicker
            isAdmin={true}
            viewerKind={viewerKind}
            models={availableModels}
            onSelect={onSelectModel}
            isRescoring={false}
          />

          <button
            type="button"
            onClick={onScoreNow}
            disabled={isScoring}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold shadow-sm border transition whitespace-nowrap ${
              isScoring
                ? "bg-indigo-400 text-white border-indigo-400 cursor-not-allowed"
                : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {isScoring ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeOpacity="0.3"
                    strokeWidth="4"
                  />
                  <path
                    d="M12 2a10 10 0 0 1 10 10"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
                <span>Scoring&hellip;</span>
              </>
            ) : (
              <span>Run risk scoring</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RunRiskScoringBanner;
